/* ============================================================
   Icon Features — behavior
   Items reveal one by one, staggered, every time the section
   scrolls into view. GSAP drives the stagger when available
   (snappier, back-out easing); a plain CSS transition fallback
   handles it otherwise. Leaving the viewport resets the items
   so the reveal plays again on re-entry.
   ============================================================ */

function initAllIconFeatures(root) {
  var scope = root || document;
  scope.querySelectorAll('[data-icon-features]').forEach(function (section) {
    if (section.dataset.ifInit === 'true') return;
    section.dataset.ifInit = 'true';
    initIconFeatures(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initAllIconFeatures();
  });
} else {
  initAllIconFeatures();
}

document.addEventListener('shopify:section:load', function (event) {
  initAllIconFeatures(event.target);
});

// GSAP loads with `defer` on the CDN <script> tag, but this file is
// loaded as a normal blocking script — so on a slow connection this
// code can run and check `window.gsap` before GSAP has actually
// finished loading, silently falling back to the plain CSS path
// even though GSAP is on the page and about to be ready. Poll
// briefly for it instead of checking once.
function waitForGsap(callback) {
  var attempts = 0;
  var maxAttempts = 40; // ~2s at 50ms intervals
  (function check() {
    if (typeof window.gsap !== 'undefined') {
      callback(true);
    } else if (attempts >= maxAttempts) {
      callback(false);
    } else {
      attempts++;
      setTimeout(check, 50);
    }
  })();
}

function initIconFeatures(section) {
  var wantsAnimation = section.dataset.animate === 'true';
  var items = section.querySelectorAll('[data-icon-item]');
  var staggerMs = parseInt(section.dataset.stagger, 10) || 45;
  var durationMs = parseInt(section.dataset.duration, 10) || 300;

  if (!wantsAnimation || !items.length) return;

  if (!('IntersectionObserver' in window)) {
    section.classList.add('is-revealed');
    return;
  }

  waitForGsap(function (gsapReady) {
    setUpReveal(section, items, staggerMs, durationMs, gsapReady);
  });
}

function setUpReveal(section, items, staggerMs, durationMs, gsapReady) {
  function hideItems() {
    if (gsapReady) {
      // Kill any in-flight reveal tween first, otherwise a quick
      // scroll-out-then-in can leave two tweens fighting over the
      // same properties.
      window.gsap.killTweensOf(items);
      window.gsap.set(items, { opacity: 0, y: 16, scale: 0.94 });
    }
    section.classList.remove('is-revealed');
  }

  function showItems() {
    if (gsapReady) {
      window.gsap.killTweensOf(items);
      window.gsap.to(items, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: durationMs / 1000,
        stagger: staggerMs / 1000,
        ease: 'power3.out'
      });
    } else {
      items.forEach(function (item, i) {
        item.style.transitionDuration = durationMs / 1000 + 's';
        item.style.transitionDelay = (i * staggerMs) / 1000 + 's';
      });
      section.classList.add('is-revealed');
    }
  }

  // Set the starting (hidden) state immediately and explicitly,
  // rather than relying only on CSS — this guarantees there's
  // always something visible to animate FROM, regardless of any
  // stylesheet load-order/specificity quirk.
  if (gsapReady) {
    window.gsap.set(items, { opacity: 0, y: 16, scale: 0.94 });
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          // Fires the instant any part of the section enters the
          // viewport (see rootMargin below), so the fast burst has
          // finished playing well before the section has fully
          // scrolled into view — instead of visually overlapping
          // the scroll and reading as a scroll-linked reveal.
          // Wait two animation frames before revealing. If the
          // section is already in (or near) the viewport when the
          // observer attaches, the browser can collapse the
          // "hidden" and "visible" style changes into one paint,
          // and the transition/tween never visibly plays. Two
          // rAFs guarantee the hidden state has actually painted
          // before we flip to visible.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              showItems();
            });
          });
        } else {
          // Leaving the viewport in either direction resets the
          // items so the stagger plays again next time they
          // scroll back into view.
          hideItems();
        }
      });
    },
    {
      threshold: 0,
      // A positive bottom margin extends the trigger zone below
      // the actual viewport edge, so the observer fires while the
      // section is still approaching from below — not only once
      // it has actually scrolled into view.
      rootMargin: '0px 0px 15% 0px'
    }
  );

  observer.observe(section);
}